import React, { useEffect, useRef, useMemo } from 'react';
import { MaterialData, MaterialFiles, MaterialProperties } from '../../../../packages/ts-interfaces';
import useTextureLoader from '../libs/useTextureLoader';
import { RepeatWrapping, SRGBColorSpace, Mesh, Object3D, MeshPhysicalMaterial } from 'three';
import { materialDefaults, cloudflareCDN } from '../libs/util';
import { useThree } from '@react-three/fiber';
import { produce } from 'immer';

interface SpotMaterialProps {
    meshRef: React.RefObject<Object3D>;
    spotMaterials?: {
        [meshName: string]: MaterialData;
    };
    defaultMaterial?: MaterialData;
    textureRepeatModifier?: number;
    scaledMesh?: boolean;
}

// Same sophisticated texture resolution logic as PhysicalMaterial
const resolveTextureSize = (
    files: MaterialFiles | undefined,
    textureType: string = 'color',
    textureSize: string = 'thumbnail',
    size: number = 1024,
    forceHighQuality: boolean,
    aspectRatio: number = 1
) => {
    if (!files) return;
    
    if (forceHighQuality) {
        console.log("Using highest quality textures for SpotMaterial");
        return files[textureType + '_original'];
    }
    
    textureType = textureType === 'color' && files['albedo_original'] ? 'albedo' : textureType;
    const k = textureSize === 'thumbnail' ? 'thumbnail' : textureSize + '_' + textureType;
    
    if (files[k]) return files[k];
    else {
        const originalKey = textureType + '_original';
        if (files[originalKey]) {
            if (files[originalKey].startsWith("data") || files[originalKey].startsWith("blob")) {
                return files[originalKey];
            } else {
                let x = "";
                if (aspectRatio < 1) {
                    x = cloudflareCDN(files[originalKey], 'width=' + size + ',format=auto');
                } else {
                    x = cloudflareCDN(files[originalKey], 'height=' + size + ',format=auto');
                }
                return x;
            }
        }
    }
};

/**
 * SpotMaterial component provides declarative material application for GLTF objects
 * with per-mesh material control. Uses the same sophisticated texture loading approach
 * as PhysicalMaterial for high-quality results.
 * 
 * Usage:
 * <SpotMaterial 
 *   meshRef={ref} 
 *   spotMaterials={spotMaterials} 
 *   defaultMaterial={physicalObject.materialData}
 *   textureRepeatModifier={textureRepeatModifier}
 * />
 */
export const SpotMaterial: React.FC<SpotMaterialProps> = ({
    meshRef,
    spotMaterials,
    defaultMaterial,
    textureRepeatModifier = 1,
    scaledMesh = false
}) => {
    const appliedRef = useRef<Set<string>>(new Set());
    const { scene, invalidate } = useThree();
    const forceHighQuality = window.location?.search.toLowerCase().includes('forcehighquality');

    // Get the primary material data to load textures for (first spot material or default)
    const primaryMaterial = useMemo(() => {
        if (spotMaterials) {
            const firstSpotMaterial = Object.values(spotMaterials)[0];
            if (firstSpotMaterial) return firstSpotMaterial;
        }
        return defaultMaterial;
    }, [spotMaterials, defaultMaterial]);

    // Load textures using the same sophisticated approach as PhysicalMaterial
    const files: MaterialFiles | undefined = primaryMaterial?.files;
    const smallColorTexture = useTextureLoader(resolveTextureSize(files, 'color', 'thumbnail', 100, false));
    const largeColorTexture = useTextureLoader(resolveTextureSize(files, 'color', 'small', 512, forceHighQuality, primaryMaterial?.aspectRatio));
    const colorTexture = largeColorTexture || smallColorTexture;
    const alphaTexture = useTextureLoader(resolveTextureSize(files, 'alpha', 'small', 512, forceHighQuality));
    const normalTexture = useTextureLoader(resolveTextureSize(files, 'normal', 'large', 1024, forceHighQuality));
    const armTexture = useTextureLoader(resolveTextureSize(files, 'arm', 'small', 512, forceHighQuality));
    const displacementTexture = null;

    const allTextures = [armTexture, colorTexture, normalTexture, alphaTexture, displacementTexture];

    // Helper function to apply material properties to a mesh
    const applyMaterialToMesh = (mesh: any, materialData: MaterialData) => {
        // console.log('🎨 SpotMaterial: Applying sophisticated material to mesh:', {
        //     meshName: mesh.name,
        //     materialType: mesh.material?.type,
        //     hasFiles: !!materialData.files
        // });

        if (!materialData?.materialProps && !materialData?.files) {
            console.log('❌ No materialProps or files found in materialData');
            return;
        }

        const materials: MaterialProperties = produce({ ...materialDefaults, ...materialData?.materialProps }, () => {
            // Apply any default modifications similar to PhysicalMaterial
        });

        // Clone the current material to avoid affecting other meshes
        if (mesh.material && !Array.isArray(mesh.material)) {
            mesh.material = mesh.material.clone();
        }

        // Apply texture wrapping AND flipping in one clean function
        const updateTextureSettings = () => {
            allTextures.forEach((t: any) => {
                if (t) {
                    // TEXTURE FLIPPING & ROTATION (Your main fix!)
                    t.flipY = false; // Fix upside-down textures
                    t.rotation = Math.PI * 1.5; // 270 degrees (90 degrees left)
                    t.center.set(0.5, 0.5); // Rotate around center
                    
                    // Set color space for color textures
                    if (t === colorTexture) {
                        t.colorSpace = SRGBColorSpace;
                    }
                    
                    // TEXTURE WRAPPING & REPEAT (Original logic)
                    if (t.repeat) {
                        t.wrapS = RepeatWrapping;
                        t.wrapT = RepeatWrapping;
                        const finalRepeatModifier = textureRepeatModifier === 0 || isNaN(textureRepeatModifier) ? 1 : textureRepeatModifier;
                        t.repeat.x = t.repeat.y = (materials.textureRepeat ?? 1) * (finalRepeatModifier ?? 1);

                        const ratio = t.source?.data?.width / t.source?.data?.height;

                        if (isNaN(ratio) || scaledMesh === true) return;
                        else if (ratio !== 1) {
                            if (ratio > 1) {
                                t.repeat.y = t.repeat.y * ratio;
                            } else {
                                t.repeat.x = t.repeat.x / ratio;
                            }
                        }
                    }
                }
            });
        };

        // Apply textures when they're loaded (same logic as PhysicalMaterial)
        const material = mesh.material as MeshPhysicalMaterial;
        if (material && colorTexture != null) {
            updateTextureSettings(); // Apply both wrapping and flipping in one go!
            
            // Set alpha test
            if (alphaTexture) {
                material.alphaTest = 0.6;
            } else {
                material.alphaTest = 0.0;
            }

            // Handle UV2 for ARM textures
            const meshGeometry = mesh.geometry;
            if (armTexture && meshGeometry && (!meshGeometry.attributes.uv2)) {
                meshGeometry.attributes.uv2 = meshGeometry.attributes.uv.clone();
            }

            // Apply textures
            if (colorTexture) {
                material.map = colorTexture;
            }
            material.aoMap = armTexture;
            material.normalMap = normalTexture;
            material.displacementMap = displacementTexture;
            material.roughnessMap = armTexture;
            material.metalnessMap = armTexture;
            material.alphaMap = alphaTexture;
            material.envMap = scene.environment;
        } else if (colorTexture === null && material) {
            // Clear textures if no color texture
            material.map = material.normalMap = material.aoMap = 
            material.roughnessMap = material.metalnessMap = 
            material.displacementMap = material.alphaMap = null;
        }

        // Apply material properties
        if (materials.color !== undefined && material.color) {
            if (typeof materials.color === 'string') {
                material.color.set(materials.color);
            } else {
                material.color.setHex(materials.color);
            }
        }
        if (materials.metalness !== undefined) {
            material.metalness = materials.metalness;
        }
        if (materials.roughness !== undefined) {
            material.roughness = materials.roughness;
        }
        if (materials.emissive && material.emissive) {
            material.emissive.setHex(materials.emissive);
        }
        if ((materials as any).emissiveIntensity !== undefined) {
            material.emissiveIntensity = (materials as any).emissiveIntensity;
        }
        if (materials.clearcoat !== undefined) {
            material.clearcoat = materials.clearcoat;
        }
        if ((materials as any).clearcoatRoughness !== undefined) {
            material.clearcoatRoughness = (materials as any).clearcoatRoughness;
        }
        if (materials.ior !== undefined) {
            material.ior = materials.ior;
        }
        if (materials.reflectivity !== undefined) {
            material.reflectivity = materials.reflectivity;
        }
        if (materials.envMapIntensity !== undefined) {
            material.envMapIntensity = materials.envMapIntensity;
        }

        material.side = 2; // DoubleSide
        material.needsUpdate = true;
        
        // console.log('✅ SpotMaterial: Sophisticated material application completed for mesh:', mesh.name);
    };

    // Effect to apply materials when meshRef or materials change
    useEffect(() => {
        if (!meshRef?.current) return;

                // console.log('🔍 SpotMaterial: Analyzing mesh structure for sophisticated material application');
        appliedRef.current.clear();

        meshRef.current.traverse((child: Object3D) => {
            if (child.type === 'Mesh') {
                const mesh = child as Mesh;
                const meshId = `${mesh.name}_${mesh.uuid}`;
                
                // Skip if already applied to avoid re-processing
                if (appliedRef.current.has(meshId)) return;
                
                // console.log(`📦 SpotMaterial: Processing mesh: "${mesh.name}"`);
                
                // Check if we have a specific material for this mesh name
                if (spotMaterials && spotMaterials[mesh.name]) {
                        // console.log(`✨ Applying sophisticated spot material to mesh: "${mesh.name}"`);
                    const materialData = spotMaterials[mesh.name];
                    applyMaterialToMesh(mesh, materialData);
                // } else if (defaultMaterial) {
                //     // console.log(`⚪ Applying sophisticated default material to mesh: "${mesh.name}"`);
                //     applyMaterialToMesh(mesh, defaultMaterial);
                // } else {
                    // console.log(`🚫 No material available for mesh: "${mesh.name}"`);
                }
                
                appliedRef.current.add(meshId);
            }
        });

        invalidate();
    }, [meshRef?.current, spotMaterials, defaultMaterial, textureRepeatModifier, scaledMesh, allTextures, scene.environment]);

    // This component doesn't render anything - it just applies materials
    return null;
}; 